import React, { useState } from "react";
import {
  UserCheck,
  ScanFace,
  RotateCw,
  Zap,
  Fingerprint,
  MonitorSmartphone,
  ServerCog,
  SlidersHorizontal,
  Check,
  X,
  ChevronDown,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, FeatureCard, CTASection, WhatsAppButton } from "../../components/marketing/Shared";

function CaptureMockup() {
  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-900/80 p-3 shadow-2xl">
      <div className="grid grid-cols-3 gap-1.5">
        {/* face crop */}
        <div className="relative overflow-hidden rounded-lg border border-exams/40 bg-neutral-950">
          <div className="aspect-[3/4] w-full" style={{ background: "radial-gradient(120% 90% at 50% 35%, hsl(var(--exams)/.16), transparent 62%)" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <ScanFace className="h-8 w-8 text-exams/80" />
          </div>
          <span className="absolute left-1 top-1 font-mono text-[8px] text-white/60">face</span>
        </div>
        {/* full frame */}
        <div className="relative col-span-2 overflow-hidden rounded-lg border border-white/10 bg-neutral-950">
          <div className="aspect-[3/2] w-full" style={{ background: "repeating-linear-gradient(135deg, rgba(255,255,255,.03) 0 8px, rgba(255,255,255,.06) 8px 16px)" }} />
          <span className="absolute left-1 top-1 font-mono text-[8px] text-white/60">full frame</span>
          <span className="absolute right-1 top-1 rounded bg-emerald-500/90 px-1 font-mono text-[8px] font-bold text-black">1 face</span>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <div className="rounded-lg bg-black/40 px-2.5 py-2">
          <div className="flex items-center justify-between font-mono text-[9px] text-white/50">
            <span>liveness</span><span className="text-emerald-400">0.98</span>
          </div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10"><span className="block h-full w-[98%] rounded-full bg-emerald-400" /></div>
        </div>
        <div className="rounded-lg bg-black/40 px-2.5 py-2">
          <div className="flex items-center justify-between font-mono text-[9px] text-white/50">
            <span>PAD</span><span className="text-emerald-400">0.94</span>
          </div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10"><span className="block h-full w-[94%] rounded-full bg-exams" /></div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-black/50 px-3 py-1.5">
        <span className="font-mono text-[10px] text-exams">● gesture: turn-head ✓</span>
        <span className="font-mono text-[10px] text-white/40">verdict: passed</span>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { t } = useI18n();
  const [faq, setFaq] = useState(0);

  const features = [
    { icon: UserCheck, k: "docmatch" },
    { icon: ScanFace, k: "passive" },
    { icon: RotateCw, k: "gesture" },
    { icon: Zap, k: "flash" },
    { icon: Fingerprint, k: "deep" },
    { icon: MonitorSmartphone, k: "hosted" },
    { icon: ServerCog, k: "gate" },
    { icon: SlidersHorizontal, k: "policy" },
  ];

  const compareRows = [
    ["Document-to-selfie face match", false, true],
    ["Passive + active anti-spoofing (PAD)", false, true],
    ["Hosted page, nothing to build", false, true],
    ["Server-side verdict, forgery-proof", false, true],
    ["Multi-frame deep anti-spoofing (PAD)", false, true],
    ["Immutable pass/fail per session", false, true],
  ];

  const faqs = [
    ["Do users need to install an app?", "No — the verification runs entirely in the browser on the hosted page you redirect to. It works across devices and operating systems."],
    ["How do you stop spoofing?", "Layered PAD: passive liveness while the user looks at the camera, an active gesture challenge, a Flashmark screen-flash pass and a deeper multi-frame analysis — all folded into one verdict."],
    ["Can I match against an ID document?", "Yes. Pass a reference photo or document crop and SecFace compares the live face using ArcFace embeddings, returning a match score alongside the liveness verdict."],
    ["How is the person's data protected?", "Users see a clear consent and recording notice on the hosted page, biometric data is minimized, and the biometric result never travels in the browser redirect — only a short-lived, single-use code."],
  ];

  return (
    <>
      <PageHero
        breadcrumb={t("nav.onboarding")}
        accent="exams"
        eyebrow={t("onb.hero.eyebrow")}
        title={t("onb.hero.title")}
        lead={t("onb.hero.lead")}
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contato" size="lg">
            {t("common.bookDemo")}
          </Button>
          <WhatsAppButton variant="outline" className="border-white/25 text-white hover:bg-white/10" />
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard key={f.k} icon={f.icon} accent="exams" title={t(`onb.f.${f.k}.title`)} desc={t(`onb.f.${f.k}.desc`)} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Capture showcase */}
      <Section alt>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Badge tone="exams">{t("onb.hero.eyebrow")}</Badge>
              <h2 className="mt-4 text-3xl">{t("onb.showcase.title")}</h2>
              <p className="mt-4 text-muted-foreground">{t("onb.showcase.desc")}</p>
              <ul className="mt-6 space-y-3">
                {["passive", "flash", "gate"].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-exams" />
                    <span className="text-sm">
                      <strong className="font-semibold">{t(`onb.f.${k}.title`)}</strong> — {t(`onb.f.${k}.desc`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <CaptureMockup />
          </div>
        </Container>
      </Section>

      {/* Compare */}
      <Section>
        <Container>
          <SectionHead center title={t("onb.compare.title")} />
          <Card className="mx-auto mt-10 max-w-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-5 py-3 text-left font-semibold"> </th>
                  <th className="px-5 py-3 text-center font-semibold text-muted-foreground">{t("onb.compare.without")}</th>
                  <th className="px-5 py-3 text-center font-semibold text-exams">{t("onb.compare.with")}</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map(([label, a, b], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-5 py-3">{label}</td>
                    <td className="px-5 py-3 text-center">
                      {a ? <Check className="mx-auto h-4 w-4 text-success" /> : <X className="mx-auto h-4 w-4 text-muted-foreground/50" />}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {b ? <Check className="mx-auto h-4 w-4 text-exams" /> : <X className="mx-auto h-4 w-4 text-muted-foreground/50" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Container>
      </Section>

      {/* FAQ */}
      <Section alt>
        <Container>
          <SectionHead center title={t("onb.faq.title")} />
          <div className="mx-auto mt-10 max-w-2xl space-y-3">
            {faqs.map(([q, a], i) => (
              <Card key={i} className="overflow-hidden">
                <button
                  onClick={() => setFaq(faq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-semibold">{q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${faq === i ? "rotate-180" : ""}`} />
                </button>
                {faq === i && <p className="px-5 pb-4 text-sm text-muted-foreground">{a}</p>}
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
